import { createSlice } from "@reduxjs/toolkit";
import * as Immutable from "immutable";
import trim from "lodash/trim";
import SearchExecutionState from "views/logic/search/SearchExecutionState";
import { parseSearch } from "views/logic/slices/searchMetadataSlice";
import GlobalOverride from "views/logic/search/GlobalOverride";
import { selectParameters } from "views/logic/slices/viewSelectors";
import {
  selectGlobalOverride,
  selectSearchTypesToSearch,
  selectSearchExecutionState,
  selectJobIds,
  selectParameterBindings
} from "views/logic/slices/searchExecutionSelectors";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import ParameterBinding from "views/logic/parameters/ParameterBinding";
import { setParameters } from "views/logic/slices/viewSlice";
const initialState = {
  searchTypesToSearch: void 0,
  executionState: SearchExecutionState.empty(),
  isLoading: false,
  result: void 0,
  jobIds: null
};
const searchExecutionSlice = createSlice({
  name: "searchExecution",
  initialState,
  reducers: {
    resetState: () => initialState,
    loading: (state) => ({
      ...state,
      isLoading: true
    }),
    stopLoading: (state) => ({
      ...state,
      isLoading: false
    }),
    finishedLoading: (state, action) => ({
      ...state,
      isLoading: false,
      result: action.payload
    }),
    updateGlobalOverride: (state, action) => ({
      ...state,
      executionState: state.executionState.toBuilder().globalOverride(action.payload).build()
    }),
    setSearchTypesToSearch: (state, action) => ({
      ...state,
      searchTypesToSearch: action.payload
    }),
    setParameterValues: (state, action) => {
      const parameterMap = action.payload;
      let { parameterBindings } = state.executionState;
      parameterMap.forEach((value, parameterName) => {
        parameterBindings = parameterBindings.set(parameterName, ParameterBinding.forValue(value));
      });
      return {
        ...state,
        executionState: state.executionState.toBuilder().parameterBindings(parameterBindings).build()
      };
    },
    setParameterBindings: (state, action) => ({
      ...state,
      executionState: state.executionState.toBuilder().parameterBindings(action.payload).build()
    }),
    addParameterBindings: (state, action) => {
      const { parameterBindings } = state.executionState;
      const newParameters = action.payload;
      const newParameterBindings = Immutable.Map(
        newParameters.filter((parameter) => !!parameter.defaultValue).map((parameter) => [parameter.name, ParameterBinding.forValue(parameter.defaultValue)])
      );
      const mergedParameterBindings = parameterBindings.merge(newParameterBindings);
      return {
        ...state,
        executionState: state.executionState.toBuilder().parameterBindings(mergedParameterBindings).build()
      };
    },
    setJobIds: (state, action) => ({
      ...state,
      jobIds: action.payload
    })
  }
});
export const {
  loading,
  stopLoading,
  finishedLoading,
  updateGlobalOverride,
  resetState,
  setSearchTypesToSearch,
  setParameterValues,
  setParameterBindings,
  setJobIds
} = searchExecutionSlice.actions;
export const searchExecutionSliceReducer = searchExecutionSlice.reducer;
export const cancelExecutedJob = () => (dispatch, getState, { searchExecutors }) => {
  const state = getState();
  const jobIds = selectJobIds(state);
  if (jobIds) {
    dispatch(setJobIds(null));
    return searchExecutors.cancelJob(jobIds);
  }
  return Promise.resolve();
};
export const executeSearchJob = ({
  jobIds,
  widgetMapping,
  page,
  perPage,
  searchExecutors,
  stopPolling
}) => (dispatch, _getState) => {
  dispatch(setJobIds(jobIds));
  dispatch(loading());
  return searchExecutors.executeJobResult({ jobIds, widgetMapping, page, perPage, stopPolling }).then(searchExecutors.resultMapper).then((result) => {
    var _a, _b, _c;
    dispatch(setJobIds(null));
    const isCanceled = (_c = (_b = (_a = result == null ? void 0 : result.result) == null ? void 0 : _a.result) == null ? void 0 : _b.execution) == null ? void 0 : _c.cancelled;
    if (isCanceled) return dispatch(stopLoading());
    return dispatch(finishedLoading(result));
  });
};
export const executeWithExecutionState = ({
  search,
  activeQuery,
  searchTypesToSearch,
  executionState,
  searchExecutors,
  widgetMapping,
  page,
  perPage,
  stopPolling
}) => (dispatch) => dispatch(parseSearch(search, searchExecutors.parse)).then(() => {
  dispatch(loading());
  dispatch(cancelExecutedJob());
  return searchExecutors.startJob(search, searchTypesToSearch, executionState, [activeQuery]);
}).then(
  (jobIds) => dispatch(executeSearchJob({ searchExecutors, jobIds, widgetMapping, page, perPage, stopPolling }))
).catch((error) => {
  dispatch(cancelExecutedJob());
  dispatch(stopLoading());
  throw error;
});
export const execute = ({
  search,
  activeQuery,
  widgetMapping,
  page,
  perPage,
  stopPolling
}) => (dispatch, getState, { searchExecutors }) => {
  const state = getState();
  const executionState = selectSearchExecutionState(state);
  const searchTypesToSearch = selectSearchTypesToSearch(state);
  return dispatch(
    executeWithExecutionState({
      search,
      activeQuery,
      searchTypesToSearch,
      executionState,
      searchExecutors,
      widgetMapping,
      page,
      perPage,
      stopPolling
    })
  );
};
export const setGlobalOverrideQuery = (queryString) => async (dispatch, getState) => {
  var _a;
  const globalOverride = (_a = selectGlobalOverride(getState())) != null ? _a : GlobalOverride.empty();
  const newGlobalOverride = globalOverride.toBuilder().query(createElasticsearchQueryString(queryString)).build();
  return dispatch(searchExecutionSlice.actions.updateGlobalOverride(newGlobalOverride));
};
export const setGlobalOverrideTimerange = (timerange) => async (dispatch, getState) => {
  var _a;
  const globalOverride = (_a = selectGlobalOverride(getState())) != null ? _a : GlobalOverride.empty();
  const newGlobalOverride = globalOverride.toBuilder().timerange(timerange).build();
  return dispatch(searchExecutionSlice.actions.updateGlobalOverride(newGlobalOverride));
};
export const setGlobalOverride = (queryString, timerange) => (dispatch, getState) => {
  var _a;
  const globalOverride = (_a = selectGlobalOverride(getState())) != null ? _a : GlobalOverride.empty();
  const newGlobalOverride = globalOverride.toBuilder().query(createElasticsearchQueryString(queryString)).timerange(timerange).build();
  return dispatch(searchExecutionSlice.actions.updateGlobalOverride(newGlobalOverride));
};
export const declareParameters = (newParameters) => async (dispatch, getState) => {
  const parameters = selectParameters(getState()).toArray();
  const newParametersArray = newParameters.valueSeq().toArray();
  await dispatch(searchExecutionSlice.actions.addParameterBindings(newParametersArray));
  return dispatch(setParameters([...parameters, ...newParametersArray]));
};
export const removeParameter = (parameterName) => async (dispatch, getState) => {
  const parameters = selectParameters(getState());
  const newParameters = parameters.filter((p) => p.name !== parameterName).toArray();
  const parameterBindings = selectParameterBindings(getState());
  const newParameterBindings = parameterBindings.remove(parameterName);
  await dispatch(setParameters(newParameters));
  return dispatch(searchExecutionSlice.actions.setParameterBindings(newParameterBindings));
};
export const updateParameter = (parameterName, newParameter) => async (dispatch, getState) => {
  const parameters = selectParameters(getState());
  const newParameters = parameters.map((p) => p.name === parameterName ? newParameter : p).toArray();
  const parameterBindings = selectParameterBindings(getState());
  if (!trim(parameterBindings.get(parameterName, ParameterBinding.empty()).value) && newParameter.defaultValue) {
    const newParameterBindings = parameterBindings.set(
      parameterName,
      ParameterBinding.forValue(newParameter.defaultValue)
    );
    await dispatch(searchExecutionSlice.actions.setParameterBindings(newParameterBindings));
  }
  return dispatch(setParameters(newParameters));
};
